import { useEffect, useRef, useState } from 'react';
import { apiError } from '@/lib/format';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, Plus, Trash2, Upload, FileDown, AlertTriangle, X, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  qk, fetchClient, patchTaxonomy, importTaxonomy, taxonomyTemplateUrl,
  type Client, type TaxonomyImportResult,
} from '@/lib/queries';
import { useIsStaff } from '@/hooks/use-role';
import { api, getToken } from '@/lib/api';
import { cn } from '@/lib/cn';

type Tab = 'primary' | 'secondary' | 'talents';

interface TalentRow {
  id?: string;
  name: string;
  primary_tag_id?: string;
  photo_url?: string;
  custom?: Record<string, string>;
}

export default function TaxonomyEditorPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const allowed = useIsStaff();
  const fileRef = useRef<HTMLInputElement>(null);

  const client = useQuery({ queryKey: qk.client(id), queryFn: () => fetchClient(id), enabled: !!id });

  const [tab, setTab] = useState<Tab>('primary');
  const [primaryLabel, setPrimaryLabel] = useState('');
  const [secondaryLabel, setSecondaryLabel] = useState('');
  const [talentLabel, setTalentLabel] = useState('');
  const [talentEnabled, setTalentEnabled] = useState(true);
  const [primaryRows, setPrimaryRows] = useState<string[]>([]);
  const [secondaryRows, setSecondaryRows] = useState<string[]>([]);
  const [talents, setTalents] = useState<TalentRow[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Excel import preview before applying
  const [preview, setPreview] = useState<TaxonomyImportResult | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);

  useEffect(() => {
    if (client.data && !hydrated) {
      const t = client.data.taxonomy;
      setPrimaryLabel(t.primary.label || 'Category');
      setSecondaryLabel(t.secondary.label || 'Sub-category');
      setTalentLabel(t.talent.label || 'Talent');
      setTalentEnabled(t.talent.enabled);
      setPrimaryRows([...(t.primary.values || [])]);
      setSecondaryRows([...(t.secondary.values || [])]);
      setTalents((client.data.talents || []).map((t) => ({
        id: t.id,
        name: t.name,
        primary_tag_id: (t as any).primary_tag_id ?? undefined,
        photo_url: (t as any).photo_url ?? undefined,
        custom: (t as any).custom ?? {},
      })));
      setHydrated(true);
    }
  }, [client.data, hydrated]);

  const save = useMutation({
    mutationFn: () => patchTaxonomy(id, {
      primary_label: primaryLabel.trim(),
      primary_values: primaryRows.map((v) => v.trim()).filter(Boolean),
      secondary_label: secondaryLabel.trim(),
      secondary_values: secondaryRows.map((v) => v.trim()).filter(Boolean),
      talent_label: talentLabel.trim(),
      talent_enabled: talentEnabled,
      talents: talents.filter((t) => t.name.trim()).map((t) => ({
        id: t.id,
        name: t.name.trim(),
        primary_tag_id: t.primary_tag_id?.trim() || undefined,
        photo_url: t.photo_url?.trim() || undefined,
        custom: t.custom,
      })),
    }),
    onSuccess: () => {
      toast.success('Taxonomy saved');
      qc.invalidateQueries({ queryKey: qk.client(id) });
      qc.invalidateQueries({ queryKey: qk.clients });
    },
    onError: (e) => toast.error(apiError(e, 'Could not save the taxonomy')),
  });

  const previewMut = useMutation({
    mutationFn: (f: File) => importTaxonomy(id, f, false),
    onSuccess: (r) => {
      setPreview(r);
      if (r.warnings.length > 0) {
        toast.warning(r.warnings.join(' '));
      } else {
        toast.success(`Parsed: ${r.primary_values.length} primary, ${r.secondary_values.length} secondary, ${r.talents.length} talents`);
      }
    },
  });

  const applyMut = useMutation({
    mutationFn: (f: File) => importTaxonomy(id, f, true),
    onSuccess: (r) => {
      toast.success('Excel applied - taxonomy replaced');
      // Refresh form state from new server values
      setPrimaryRows([...r.primary_values]);
      setSecondaryRows([...r.secondary_values]);
      setTalents(r.talents.map((t) => ({
        id: t.id, name: t.name,
        primary_tag_id: t.primary_tag_id ?? undefined,
        photo_url: t.photo_url ?? undefined,
        custom: t.custom ?? {},
      })));
      setPreview(null); setImportFile(null);
      qc.invalidateQueries({ queryKey: qk.client(id) });
      qc.invalidateQueries({ queryKey: qk.clients });
    },
  });

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Pick a .xlsx file');
      return;
    }
    setImportFile(f);
    previewMut.mutate(f);
  }

  function downloadTemplate() {
    // Manually fetch with auth header (anchor tag wouldn't carry the JWT)
    const url = taxonomyTemplateUrl(id);
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`Download failed: ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `taxonomy-${client.data?.name?.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => toast.error(e.message));
  }

  if (!allowed) return <Navigate to="/" replace />;
  if (client.isLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!client.data) return <Navigate to="/clients" replace />;
  const c = client.data;

  return (
    <div>
      <Link to={`/clients/${c.id}`} className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
        <ArrowLeft size={12} /> Back to {c.name}
      </Link>

      <PageHeader
        title="Edit taxonomy"
        subtitle={`${c.name} — categories, sub-categories, and talents the team and client see across all creatives.`}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" icon={<FileDown size={14} />} onClick={downloadTemplate}>
              Download Excel
            </Button>
            <Button variant="secondary" icon={<Upload size={14} />} onClick={() => fileRef.current?.click()}>
              Upload Excel
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={handleFile} />
            <Button onClick={() => save.mutate()} loading={save.isPending} icon={<Save size={14} />}>
              Save
            </Button>
          </div>
        }
      />

      {/* Preview modal of Excel import */}
      {preview && importFile && (
        <Card className="mb-5 border-accent-400/40 bg-accent-600/5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-accent-400" />
              <h3 className="text-sm font-medium text-muted-strong">
                Preview - <span className="font-mono text-xs">{importFile.name}</span>
              </h3>
            </div>
            <button onClick={() => { setPreview(null); setImportFile(null); }} className="text-muted hover:text-muted-strong">
              <X size={16} />
            </button>
          </div>
          {preview.warnings.length > 0 && (
            <div className="mb-3 text-xs text-yellow-400 inline-flex items-center gap-2">
              <AlertTriangle size={12} /> {preview.warnings.join(' · ')}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs mb-4">
            <PreviewBlock title={`${primaryLabel} (${preview.primary_values.length})`} items={preview.primary_values} />
            <PreviewBlock title={`${secondaryLabel} (${preview.secondary_values.length})`} items={preview.secondary_values} />
            <PreviewBlock title={`${talentLabel}s (${preview.talents.length})`} items={preview.talents.map((t) => t.name)} />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <Button variant="ghost" onClick={() => { setPreview(null); setImportFile(null); }}>Cancel</Button>
            <Button
              loading={applyMut.isPending}
              onClick={() => importFile && applyMut.mutate(importFile)}
            >
              Apply (replaces current)
            </Button>
          </div>
        </Card>
      )}

      {/* Per-axis labels */}
      <Card className="mb-5">
        <h3 className="font-medium text-muted-strong text-sm mb-3">Axis labels (what users see)</h3>
        <p className="text-xs text-muted mb-4">
          The same fields work for any vertical. Pick labels that match this client's world:
          clinic = Specialty/Treatment/Doctor, fashion = Category/Style/Model, F&amp;B = Cuisine/Dish/Chef.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input label="Primary label" value={primaryLabel} onChange={(e) => setPrimaryLabel(e.target.value)} />
          <Input label="Sub-category label" value={secondaryLabel} onChange={(e) => setSecondaryLabel(e.target.value)} />
          <Input label="Talent label" value={talentLabel} onChange={(e) => setTalentLabel(e.target.value)} disabled={!talentEnabled} />
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-muted-strong">
          <input type="checkbox" checked={talentEnabled} onChange={(e) => setTalentEnabled(e.target.checked)} className="accent-accent-400" />
          Track talent for this client
        </label>
      </Card>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-3">
        <TabBtn label={`${primaryLabel}`}     count={primaryRows.length}   active={tab === 'primary'}   onClick={() => setTab('primary')} />
        <TabBtn label={`${secondaryLabel}`}   count={secondaryRows.length} active={tab === 'secondary'} onClick={() => setTab('secondary')} />
        <TabBtn label={`${talentLabel}s`}     count={talents.length}       active={tab === 'talents'}   onClick={() => setTab('talents')} disabled={!talentEnabled} />
      </div>

      {tab === 'primary' && (
        <SimpleListEditor
          label={primaryLabel}
          rows={primaryRows}
          onChange={setPrimaryRows}
          placeholder={`e.g. Dental, Trousers, Italian`}
          hint="One value per row. These appear in the per-creative dropdown for this client."
        />
      )}

      {tab === 'secondary' && (
        <SimpleListEditor
          label={secondaryLabel}
          rows={secondaryRows}
          onChange={setSecondaryRows}
          placeholder={`e.g. veneer, denim, pasta`}
          hint="One value per row. Currently flat (not bound to a primary - that linkage ships in a later phase)."
        />
      )}

      {tab === 'talents' && (
        <TalentsEditor
          rows={talents}
          onChange={setTalents}
          primaryOptions={primaryRows}
          talentLabel={talentLabel}
        />
      )}
    </div>
  );
}

function TabBtn({ label, count, active, onClick, disabled }: { label: string; count: number; active: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'text-xs px-4 py-2 rounded-lg border transition capitalize',
        active
          ? 'border-accent-400 bg-accent-600/20 text-accent-400'
          : 'border-slate-800 text-muted hover:text-muted-strong',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}

function SimpleListEditor({
  label, rows, onChange, placeholder, hint,
}: { label: string; rows: string[]; onChange: (r: string[]) => void; placeholder?: string; hint?: string }) {
  function addRow() { onChange([...rows, '']); }
  function setAt(i: number, v: string) { const c = [...rows]; c[i] = v; onChange(c); }
  function removeAt(i: number) { onChange(rows.filter((_, idx) => idx !== i)); }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-muted-strong text-sm">{label}</h3>
        <Button size="sm" icon={<Plus size={12} />} onClick={addRow}>Add row</Button>
      </div>
      {hint && <p className="text-xs text-muted mb-4">{hint}</p>}

      {rows.length === 0 && (
        <div className="text-center py-8 text-muted text-sm border border-dashed border-slate-800 rounded-lg">
          No rows yet. Click <strong>Add row</strong> or upload an Excel.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-muted w-6 text-right">{i + 1}</span>
            <input
              value={v}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 h-9 rounded-lg border border-slate-800 bg-bg px-3 text-sm text-muted-strong placeholder:text-muted/40 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40"
            />
            <button onClick={() => removeAt(i)} className="p-1.5 rounded-lg text-muted hover:text-red-400 hover:bg-red-500/10 transition" title="Remove">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TalentsEditor({
  rows, onChange, primaryOptions, talentLabel,
}: { rows: TalentRow[]; onChange: (r: TalentRow[]) => void; primaryOptions: string[]; talentLabel: string }) {
  function addRow() { onChange([...rows, { name: '', custom: {} }]); }
  function setAt(i: number, patch: Partial<TalentRow>) {
    const c = [...rows]; c[i] = { ...c[i], ...patch }; onChange(c);
  }
  function setCustom(i: number, key: string, value: string) {
    const c = [...rows];
    c[i] = { ...c[i], custom: { ...(c[i].custom || {}), [key]: value } };
    onChange(c);
  }
  function removeAt(i: number) { onChange(rows.filter((_, idx) => idx !== i)); }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-muted-strong text-sm">{talentLabel}s</h3>
        <Button size="sm" icon={<Plus size={12} />} onClick={addRow}>Add {talentLabel.toLowerCase()}</Button>
      </div>

      {rows.length === 0 && (
        <div className="text-center py-8 text-muted text-sm border border-dashed border-slate-800 rounded-lg">
          No {talentLabel.toLowerCase()}s yet.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((t, i) => (
          <div key={i} className="rounded-lg border border-slate-800 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted uppercase tracking-wider">{talentLabel} {i + 1}</span>
              <button onClick={() => removeAt(i)} className="p-1 text-muted hover:text-red-400 hover:bg-red-500/10 rounded transition">
                <Trash2 size={12} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input
                label="Name"
                value={t.name}
                onChange={(e) => setAt(i, { name: e.target.value })}
                placeholder={`e.g. Dr. Sara Al-Hamad`}
              />
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="text-xs text-muted">Primary tag (optional)</span>
                <select
                  value={t.primary_tag_id || ''}
                  onChange={(e) => setAt(i, { primary_tag_id: e.target.value || undefined })}
                  className="h-10 rounded-lg border border-slate-800 bg-bg px-3 text-sm text-muted-strong focus:outline-none focus:border-accent-400"
                >
                  <option value="">(none)</option>
                  {primaryOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input
                label="Title (optional)"
                value={t.custom?.title || ''}
                onChange={(e) => setCustom(i, 'title', e.target.value)}
                placeholder={`e.g. Cosmetic Dentist`}
              />
              <Input
                label="Photo URL (optional)"
                value={t.photo_url || ''}
                onChange={(e) => setAt(i, { photo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PreviewBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-bg/50 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-muted/60 italic text-[11px]">empty</div>
      ) : (
        <ul className="text-[11px] text-muted-strong space-y-0.5 max-h-32 overflow-y-auto">
          {items.slice(0, 12).map((v, i) => <li key={i}>· {v}</li>)}
          {items.length > 12 && <li className="text-muted">... +{items.length - 12} more</li>}
        </ul>
      )}
    </div>
  );
}
